import React from "react";
import { Button } from "@/components/ui/button";
import { CalendarDays, Clock3, Eye, Pencil } from "lucide-react";
import { cn } from "@/shared/utils/cn";
import type { LeaveRequestDisplayItem } from "../types";

interface LeaveRequestCardProps {
  request: LeaveRequestDisplayItem;
  onEdit?: (request: LeaveRequestDisplayItem) => void;
  onOpenPendingApproval?: (request: LeaveRequestDisplayItem) => void;
  onViewDetails?: (request: LeaveRequestDisplayItem) => void;
  className?: string;
  highlighted?: boolean;
}

function getStatusPresentation(status: string) {
  switch (status) {
    case "approved":
      return {
        label: "Approved",
        badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-300",
        cardClass: "border-blue-200",
        hint: "Confirmed",
        hintClass: "bg-indigo-50 text-slate-700",
        iconWrapClass: "bg-indigo-100",
        iconClass: "text-indigo-500",
        summaryIconClass: "text-indigo-600",
      };
    case "rejected":
      return {
        label: "Rejected",
        badgeClass: "bg-red-50 text-red-700 border-red-300",
        cardClass: "border-red-200",
        hint: "Rejected",
        hintClass: "bg-red-50 text-red-700",
        iconWrapClass: "bg-red-100",
        iconClass: "text-red-500",
        summaryIconClass: "text-red-600",
      };
    case "pending":
    default:
      return {
        label: "Pending",
        badgeClass: "bg-amber-50 text-amber-700 border-amber-300",
        cardClass: "border-amber-200",
        hint: "Awaiting approval",
        hintClass: "bg-amber-50 text-slate-700",
        iconWrapClass: "bg-amber-100",
        iconClass: "text-amber-600",
        summaryIconClass: "text-amber-600",
      };
  }
}

function formatDisplayDate(isoDate: string): string {
  const parsed = new Date(`${isoDate}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(parsed);
}

function resolveLeaveTypeLabel(leaveTypeId: number): string {
  switch (leaveTypeId) {
    case 1:
      return "Holiday";
    case 2:
      return "Sick Leave";
    case 3:
      return "Compassionate Leave";
    default:
      return "Leave";
  }
}

export function LeaveRequestCard({
  request,
  onEdit,
  onOpenPendingApproval,
  onViewDetails,
  className,
  highlighted = false,
}: LeaveRequestCardProps) {
  const status = getStatusPresentation(request.status);
  const isPending = request.status === "pending";
  const isPendingClickable = isPending && onOpenPendingApproval != null;
  const leaveTypeLabel = resolveLeaveTypeLabel(request.leave_type_id);
  const actionLabel = isPending ? "Edit" : "View Details";

  return (
    <article
      id={`leave-request-${request.leave_request_id}`}
      className={cn(
        "bg-white rounded-2xl shadow-sm border p-4 space-y-4",
        isPendingClickable && "cursor-pointer hover:shadow-md transition-shadow",
        status.cardClass,
        highlighted && "ring-2 ring-blue-500 ring-offset-1",
        className
      )}
      onClick={isPendingClickable ? () => onOpenPendingApproval(request) : undefined}
      data-testid={`leave-request-card-${request.leave_request_id}`}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className={cn("flex-shrink-0 rounded-xl p-2.5", status.iconWrapClass)}>
            <CalendarDays className={cn("h-5 w-5", status.iconClass)} aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900 truncate m-0">{leaveTypeLabel}</h3>
            <p className="text-sm text-gray-500 truncate">
              {request.booking_unit === "half" ? "Half day" : "All Day"}
            </p>
          </div>
        </div>
        <span
          className={cn(
            "text-xs font-medium px-2.5 py-1 rounded-full border",
            status.badgeClass
          )}
          data-testid={`leave-request-status-${request.leave_request_id}`}
        >
          {status.label}
        </span>
      </header>

      <div className="grid grid-cols-2 gap-3 bg-white">
        <div className="flex items-start gap-2 min-w-0">
          <div className="rounded-lg p-2 bg-slate-100">
            <CalendarDays className="h-4 w-4 text-slate-500" />
          </div>
          <div>
            <p className="text-sm text-slate-500">Start</p>
            <p className="text-base font-semibold text-slate-900 leading-tight">
              {formatDisplayDate(request.start_date)}
            </p>
          </div>
        </div>
        <div className="flex items-start gap-2 min-w-0">
          <div className="rounded-lg p-2 bg-slate-100">
            <CalendarDays className="h-4 w-4 text-slate-500" />
          </div>
          <div>
            <p className="text-sm text-slate-500">End</p>
            <p className="text-base font-semibold text-slate-900 leading-tight">
              {formatDisplayDate(request.end_date)}
            </p>
          </div>
        </div>
      </div>

      <div className={cn("rounded-xl p-3 text-sm font-medium", status.hintClass)}>
        <div className="flex items-center gap-2">
          <Clock3 className={cn("h-4 w-4", status.summaryIconClass)} />
          <span>
            {request.requested_days} {request.requested_days === 1 ? "day" : "days"} · {status.hint}
          </span>
        </div>
      </div>

      {isPending && onEdit ? (
        <Button
          type="button"
          variant="outline"
          className="w-full rounded-xl bg-slate-100 border-slate-100 text-slate-700 hover:bg-slate-200"
          onClick={(event) => {
            event.stopPropagation();
            onEdit(request);
          }}
          data-testid={`leave-request-edit-${request.leave_request_id}`}
        >
          <Pencil className="h-4 w-4 mr-2" />
          {actionLabel}
        </Button>
      ) : null}

      {!isPending ? (
        <Button
          type="button"
          variant="outline"
          className="w-full rounded-xl bg-slate-100 border-slate-100 text-slate-700 hover:bg-slate-200"
          onClick={(event) => {
            event.stopPropagation();
            onViewDetails?.(request);
          }}
          data-testid={`leave-request-view-${request.leave_request_id}`}
        >
          <Eye className="h-4 w-4 mr-2" />
          {actionLabel}
        </Button>
      ) : null}
    </article>
  );
}
